import { useEffect, useState } from "react";
import { Row, Card, Button } from "react-bootstrap";
import { Calendar, MapPin } from "lucide-react";
import TaxSaleDetailsModal from "./TaxSaleDetailsModal";
import { useGetCountyDetailsByStateAndCountyQuery, useGetCountySalesQuery } from "../../redux/countySlice";
import "./ScheduledSales.css";
import { getContactInfo } from "../../utilities/contactInfoUtils";

const saleTypeBadgeMap = {
  A: "bg-primary-subtle text-primary",
  O: "bg-primary-subtle text-primary",
  F: "purple",
  C: "bg-warning-subtle text-warning",
  D: "bg-success-subtle text-success",
};

function SaleRow({ date, mode, sale, onView }) {
  function formatLongDate(mmddyyyy) {
    const [month, day, year] = mmddyyyy?.split("/");
    const date = new Date(year, month - 1, day);

    return new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date);
  }

  return (
    <Card className="saleRow w-100">
      <Card.Body className="d-flex flex-wrap align-items-center justify-content-between gap-2 p-2 p-sm-3 orange-hover-card">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <span
            className={`badge rounded-pill ${saleTypeBadgeMap[sale.SaleType] || "bg-secondary-subtle text-secondary"}`}
          >
            {sale.SaleTypeDescription}
          </span>
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center gap-1">
              <Calendar size={16} className="orange" />
              <span className="fs-8">{formatLongDate(date)}</span>
            </div>
            <div className="d-flex align-items-center gap-1">
              <MapPin size={16} className="orange" />
              <span className="fs-8">{mode}</span>
            </div>
          </div>
        </div>
        <Button className="viewDetailsBtn flex-shrink-0 ms-auto" onClick={onView}>
          <span className="fs-8">View Details</span>
        </Button>
      </Card.Body>
    </Card>
  );
}

const ScheduledSales = ({ stateCode, countyName }) => {
  const tabInfo = [
    { eventKey: "taxSales", title: "Tax Sales", saleType: "A", param: "tax" },
    { eventKey: "foreclosureSales", title: "Foreclosure Sales", saleType: "F", param: "foreclosure" },
    { eventKey: "deedSales", title: "Deed Sales", saleType: "D", param: "deed" },
    { eventKey: "certSales", title: "Certificate Sales", saleType: "C", param: "certificate" },
  ];
  const [sales, setSales] = useState([]);
  const [selectedSale, setSelectedSale] = useState(null);

  const { data, error: salesError, isLoading: salesLoading } = useGetCountySalesQuery({ stateCode, countyName });
  const {
    data: { sheriffDetails, tsmDetails } = {},
    error: countyError,
    isLoading: countyLoading,
  } = useGetCountyDetailsByStateAndCountyQuery({ stateCode, countyName });

  const handleModalClose = () => {
    setSelectedSale(null);
  };

  const contacts = selectedSale ? getContactInfo(selectedSale.SaleType, tsmDetails, stateCode) : [];

  useEffect(() => {
    if (!data) return;

    const sorted = [...data].sort((a, b) => {
      const [am, ad, ay] = a.Date.split("/");
      const [bm, bd, by] = b.Date.split("/");
      const dateA = new Date(Number(ay), Number(am) - 1, Number(ad));
      const dateB = new Date(Number(by), Number(bm) - 1, Number(bd));
      return dateA - dateB;
    });

    const mapped = sorted.map((sale) => ({
      date: sale.Date,
      mode: sale.Type,
      raw: sale,
    }));

    setSales(mapped);
  }, [data]);

  if (salesLoading || countyLoading) return <p>Loading...</p>;
  if (salesError || countyError)
    return <p className="text-danger">Unable to load scheduled sales. Please try again later.</p>;

  return (
    <>
      <Row>
        <h5>All Upcoming Scheduled Sales</h5>

        {sales.length > 0 ? (
          <div className="d-flex flex-column gap-3">
            {sales.map((s, idx) => (
              <SaleRow
                key={`${s.date}-${idx}`}
                date={s.date}
                mode={s.mode}
                sale={s.raw}
                onView={() => setSelectedSale(s.raw)}
              />
            ))}
          </div>
        ) : (
          <Card className="h-100 w-100 p-4 d-flex align-items-center justify-content-center">
            {"No upcoming scheduled sales."}
          </Card>
        )}
      </Row>
      <TaxSaleDetailsModal
        sale={selectedSale}
        onHide={handleModalClose}
        contacts={contacts}
        saleTypeParam={tabInfo.find((tab) => tab.saleType === selectedSale?.SaleType)?.param}
        sheriffDetails={sheriffDetails}
        tsmDetails={tsmDetails}
      />
    </>
  );
};

export default ScheduledSales;
